import React, { useRef, useEffect, useState } from 'react';
import { branding } from '../../constants/branding';

interface FlowNode {
  id: string;
  address: string;
  amount: number;
  direction: 'in' | 'out' | 'root';
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  isExchange: boolean;
  isSanctioned: boolean;
  step: number;
  timestamp: string;
  transactionHash?: string;
  level: number; // Distance from root (0 = root, 1 = direct connections, 2 = second level, etc.)
}

interface FlowConnection {
  from: string;
  to: string;
  amount: number;
  direction: 'in' | 'out';
  step: number;
  timestamp: string;
  transactionHash: string;
  level: number;
}

interface CryptoTransactionFlowProps {
  traceData: {
    rootAddress: string;
    traceSteps: any[];
    totalAmount: number;
    riskScore: number;
    summary: {
      totalNodes: number;
      inConnections: number;
      outConnections: number;
      totalConnections: number;
    };
  };
  onRenderComplete?: () => void;
}

const CryptoTransactionFlow: React.FC<CryptoTransactionFlowProps> = ({ traceData, onRenderComplete }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastPan, setLastPan] = useState({ x: 0, y: 0 });

  // Handle wheel zoom with proper event management
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (svgRef.current && svgRef.current.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
        
        // Handle zoom
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoom(prev => Math.max(0.1, Math.min(5, prev * delta)));
      }
    };

    const svgElement = svgRef.current;
    if (svgElement) {
      svgElement.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        svgElement.removeEventListener('wheel', handleWheel);
      };
    }
  }, []);

  // Notify parent when rendering is complete
  useEffect(() => {
    if (traceData && onRenderComplete) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        onRenderComplete();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [traceData, onRenderComplete]);

  // Process trace data into flow nodes and connections
  const processFlowData = (): { nodes: FlowNode[], connections: FlowConnection[] } => {
    const nodes: FlowNode[] = [];
    const connections: FlowConnection[] = [];
    const nodeMap = new Map<string, FlowNode>();

    if (!traceData?.traceSteps) return { nodes, connections };

    console.log('Processing flow data with', traceData.traceSteps.length, 'steps');

    // Add root node at level 0
    const rootNode: FlowNode = {
      id: 'root',
      address: traceData.rootAddress,
      amount: traceData.totalAmount,
      direction: 'root',
      riskLevel: 'LOW',
      isExchange: false,
      isSanctioned: false,
      step: 0,
      timestamp: new Date().toISOString(),
      level: 0
    };
    nodeMap.set('root', rootNode);
    nodes.push(rootNode);

    // Group trace steps by level (distance from root)
    const levelGroups = new Map<number, any[]>();
    
    traceData.traceSteps.forEach((step) => {
      const level = step.step || 1;
      if (!levelGroups.has(level)) {
        levelGroups.set(level, []);
      }
      levelGroups.get(level)!.push(step);
    });

    // Process each level
    levelGroups.forEach((steps, level) => {
      steps.forEach((step, index) => {
        const address = step.fromAddress || step.toAddress || step.address;
        if (!address) return;

        const nodeId = `level-${level}-${index}`;
        if (!nodeMap.has(nodeId)) {
          // Handle missing or zero amounts for flow nodes
          let nodeAmount = step.amount || 0;
          if (nodeAmount === 0 && traceData.totalAmount > 0) {
            // If individual amounts are 0 but we have a total, distribute it across steps
            nodeAmount = traceData.totalAmount / traceData.traceSteps.length;
          } else if (nodeAmount === 0) {
            // Use a more realistic default amount for Bitcoin transactions
            nodeAmount = 0.01; // Use 0.01 BTC as a more realistic default
          }

          const node: FlowNode = {
            id: nodeId,
            address: address,
            amount: nodeAmount,
            direction: step.direction || 'out',
            riskLevel: step.riskLevel || (step.isHighRisk ? 'HIGH' : 'LOW'),
            isExchange: step.isExchange || false,
            isSanctioned: step.isSanctioned || false,
            step: step.step || level,
            timestamp: step.timestamp || new Date().toISOString(),
            transactionHash: step.transactionHash,
            level: level
          };
          nodeMap.set(nodeId, node);
          nodes.push(node);

          // Create connection - handle Bitcoin case where fromAddress might be empty
          if (step.toAddress) {
            let fromId: string;
            
            if (step.fromAddress) {
              // Normal case: both from and to addresses exist
              fromId = step.fromAddress.toLowerCase() === traceData.rootAddress.toLowerCase() ? 'root' : 
                      `level-${level - 1}-${index}`;
            } else {
              // Bitcoin case: empty fromAddress, connect from root or previous level
              fromId = level === 1 ? 'root' : `level-${level - 1}-${index}`;
            }
            
            const toId = nodeId;

            // Handle missing or zero amounts for connections
            let connectionAmount = step.amount || 0;
            if (connectionAmount === 0 && traceData.totalAmount > 0) {
              // If individual amounts are 0 but we have a total, distribute it across steps
              connectionAmount = traceData.totalAmount / traceData.traceSteps.length;
            } else if (connectionAmount === 0) {
              // Use a more realistic default amount for Bitcoin transactions
              connectionAmount = 0.01; // Use 0.01 BTC as a more realistic default
            }

            connections.push({
              from: fromId,
              to: toId,
              amount: connectionAmount,
              direction: step.direction || 'out',
              step: step.step || level,
              timestamp: step.timestamp || new Date().toISOString(),
              transactionHash: step.transactionHash || '',
              level: level
            });
          }
        }
      });
    });

    console.log('Processed flow:', nodes.length, 'nodes and', connections.length, 'connections');
    return { nodes, connections };
  };

  const { nodes, connections } = processFlowData();

  // Calculate positions for flow layout (horizontal flow from left to right)
  const calculateFlowPositions = () => {
    const positions: { [key: string]: { x: number; y: number } } = {};
    const levelWidth = 120; // Reduced from 200 to fit more levels
    const nodeSpacing = 60; // Reduced from 80 to fit more nodes
    const startX = 80;
    const startY = 300;

    // Group nodes by level
    const nodesByLevel = new Map<number, FlowNode[]>();
    nodes.forEach(node => {
      if (!nodesByLevel.has(node.level)) {
        nodesByLevel.set(node.level, []);
      }
      nodesByLevel.get(node.level)!.push(node);
    });

    // Position nodes by level
    nodesByLevel.forEach((levelNodes, level) => {
      const x = startX + (level * levelWidth);
      const totalHeight = levelNodes.length * nodeSpacing;
      const startYForLevel = startY - (totalHeight / 2);

      levelNodes.forEach((node, index) => {
        const y = startYForLevel + (index * nodeSpacing);
        positions[node.id] = { x, y };
      });
    });

    return positions;
  };

  const positions = calculateFlowPositions();

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'LOW': return '#4CAF50';
      case 'MEDIUM': return '#FF9800';
      case 'HIGH': return '#F44336';
      case 'CRITICAL': return '#9C27B0';
      default: return '#9E9E9E';
    }
  };

  const getNodeSize = (amount: number) => {
    const maxAmount = Math.max(...nodes.map(n => n.amount));
    const minSize = 20;
    const maxSize = 50;
    if (maxAmount === 0) return minSize;
    return minSize + (amount / maxAmount) * (maxSize - minSize);
  };

  const formatAddress = (address: string) => {
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(amount);
  };

  const handleNodeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const nodeId = e.currentTarget.id;
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      console.log('Flow node clicked:', node);
      setSelectedNode(node);
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<SVGElement>) => {
    const target = e.currentTarget as SVGElement;
    target.style.opacity = '0.8';
    target.style.strokeWidth = '3';
  };

  const handleMouseLeave = (e: React.MouseEvent<SVGElement>) => {
    const target = e.currentTarget as SVGElement;
    target.style.opacity = '1';
    target.style.strokeWidth = '2';
  };

  // If no steps, still render a minimal chart with only the root node
  if (!traceData?.traceSteps || traceData.traceSteps.length === 0) {
    traceData = { ...traceData, traceSteps: [] } as any;
  }

  return (
    <div 
      className="crypto-transaction-flow"
      style={{
        width: '100%',
        height: '600px',
        border: `1px solid ${branding.borderColor}`,
        borderRadius: 8,
        backgroundColor: '#fafafa',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'Arial, sans-serif',
        color: branding.textColor
      }}
    >
      {/* Controls */}
      <div style={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 8
      }}>
        <div style={{
          display: 'flex',
          gap: 4,
          backgroundColor: 'white',
          padding: 4,
          borderRadius: 6,
          border: `1px solid ${branding.borderColor}`
        }}>
          <button
            onClick={() => setZoom(prev => Math.min(prev * 1.2, 5))}
            style={{
              padding: '4px 8px',
              backgroundColor: branding.primaryColor,
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12
            }}
          >
            +
          </button>
          <button
            onClick={() => setZoom(prev => Math.max(prev / 1.2, 0.1))}
            style={{
              padding: '4px 8px',
              backgroundColor: branding.primaryColor,
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12
            }}
          >
            -
          </button>
          <button
            onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
            style={{
              padding: '4px 8px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12
            }}
          >
            Reset
          </button>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '4px 8px',
          borderRadius: 4,
          border: `1px solid ${branding.borderColor}`,
          fontSize: 10,
          textAlign: 'center'
        }}>
          Zoom: {Math.round(zoom * 100)}%
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '4px 8px',
          borderRadius: 4,
          border: `1px solid ${branding.borderColor}`,
          fontSize: 10,
          textAlign: 'center'
        }}>
          Drag to pan
        </div>
      </div>

      {/* Legend */}
      <div style={{
        position: 'absolute',
        top: 10,
        left: 10,
        backgroundColor: 'white',
        border: `1px solid ${branding.borderColor}`,
        borderRadius: 6,
        padding: 8,
        fontSize: 10,
        zIndex: 10
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Flow Legend</div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#4CAF50', borderRadius: '50%', marginRight: 6 }}></div>
          Low Risk
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#FF9800', borderRadius: '50%', marginRight: 6 }}></div>
          Medium Risk
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#F44336', borderRadius: '50%', marginRight: 6 }}></div>
          High Risk
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#9C27B0', borderRadius: '50%', marginRight: 6 }}></div>
          Critical Risk
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#2196F3', borderRadius: '50%', marginRight: 6 }}></div>
          Exchange
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 12, height: 12, backgroundColor: '#000', borderRadius: '50%', marginRight: 6 }}></div>
          Sanctioned
        </div>
      </div>

      {/* SVG Flow Diagram */}
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox="0 0 2000 600"
        style={{
          transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
          transformOrigin: 'center center',
          transition: isDragging ? 'none' : 'transform 0.3s ease',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            setIsDragging(true);
            setDragStart({ x: e.clientX, y: e.clientY });
            setLastPan({ x: pan.x, y: pan.y });
          }
        }}
        onMouseMove={(e) => {
          if (isDragging) {
            const deltaX = e.clientX - dragStart.x;
            const deltaY = e.clientY - dragStart.y;
            setPan({
              x: lastPan.x + deltaX,
              y: lastPan.y + deltaY
            });
          }
        }}
        onMouseUp={() => {
          setIsDragging(false);
        }}
        onMouseLeave={() => {
          setIsDragging(false);
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && !isDragging) {
            setSelectedNode(null);
          }
        }}
      >
        {/* Connection Lines */}
        {connections.map((connection, index) => {
          const fromPos = positions[connection.from];
          const toPos = positions[connection.to];
          if (!fromPos || !toPos) return null;

          const strokeColor = connection.direction === 'in' ? '#4CAF50' : '#F44336';
          const strokeWidth = Math.max(2, Math.min(6, Math.log(connection.amount + 1) * 2));
          const midX = (fromPos.x + toPos.x) / 2;
          const midY = (fromPos.y + toPos.y) / 2;

          return (
            <g key={`flow-connection-${index}`}>
              {/* Connection line */}
              <line
                x1={fromPos.x}
                y1={fromPos.y}
                x2={toPos.x}
                y2={toPos.y}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                opacity={0.7}
                strokeDasharray={connection.direction === 'in' ? '5,5' : 'none'}
              />

              {/* Arrow head */}
              <polygon
                points={`${toPos.x - 8},${toPos.y - 4} ${toPos.x},${toPos.y} ${toPos.x - 8},${toPos.y + 4}`}
                fill={strokeColor}
                transform={`rotate(${Math.atan2(toPos.y - fromPos.y, toPos.x - fromPos.x) * 180 / Math.PI} ${toPos.x} ${toPos.y})`}
              />

              {/* Amount label */}
              <rect
                x={midX - 25}
                y={midY - 8}
                width="50"
                height="16"
                fill="white"
                stroke={strokeColor}
                strokeWidth="1"
                rx="4"
                opacity={0.9}
                style={{ pointerEvents: 'none' }}
              />
              <text
                x={midX}
                y={midY + 2}
                textAnchor="middle"
                fill="#333"
                fontSize="8"
                fontWeight="600"
                style={{ pointerEvents: 'none' }}
              >
                {formatAmount(connection.amount)}
              </text>

              {/* Level indicator - Now clickable */}
              <circle
                cx={midX + 20}
                cy={midY - 10}
                r="6"
                fill={strokeColor}
                stroke="white"
                strokeWidth="1"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Flow step indicator clicked:', {
                    level: connection.level,
                    connection,
                    transactionHash: connection.transactionHash
                  });
                  // Create a temporary node for the step indicator
                  const stepNode = {
                    id: `flow-step-${connection.level}-${index}`,
                    address: `${connection.from} → ${connection.to}`,
                    amount: connection.amount,
                    direction: connection.direction,
                    riskLevel: 'MEDIUM' as const,
                    isExchange: false,
                    isSanctioned: false,
                    step: connection.level,
                    timestamp: connection.timestamp,
                    transactionHash: connection.transactionHash,
                    level: connection.level
                  };
                  setSelectedNode(stepNode);
                }}
                onMouseEnter={(e) => {
                  const target = e.currentTarget as SVGElement;
                  target.style.opacity = '0.8';
                  target.style.strokeWidth = '2';
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget as SVGElement;
                  target.style.opacity = '1';
                  target.style.strokeWidth = '1';
                }}
              />
              <text
                x={midX + 20}
                y={midY - 7}
                textAnchor="middle"
                fill="white"
                fontSize="7"
                fontWeight="600"
                style={{ pointerEvents: 'none' }}
              >
                {connection.level}
              </text>
            </g>
          );
        })}

        {/* Flow Nodes */}
        {nodes.map((node) => {
          const nodePos = positions[node.id];
          if (!nodePos) return null;

          const nodeSize = getNodeSize(node.amount);
          const riskColor = getRiskColor(node.riskLevel);
          const fillColor = node.isSanctioned ? '#000' : 
                           node.isExchange ? '#2196F3' : riskColor;

          return (
            <g key={node.id} id={node.id} onClick={handleNodeClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ cursor: 'pointer' }}>
              {/* Node Shape */}
              <circle
                cx={nodePos.x}
                cy={nodePos.y}
                r={nodeSize / 2}
                fill={fillColor}
                stroke="white"
                strokeWidth="2"
              />

              {/* Level indicator for non-root nodes */}
              {node.level > 0 && (
                <text
                  x={nodePos.x + nodeSize / 2 - 5}
                  y={nodePos.y - nodeSize / 2 + 5}
                  textAnchor="middle"
                  fill="white"
                  fontSize="8"
                  fontWeight="600"
                  style={{ pointerEvents: 'none' }}
                >
                  {node.level}
                </text>
              )}

              {/* Root node label */}
              {node.id === 'root' && (
                <text
                  x={nodePos.x}
                  y={nodePos.y + 4}
                  textAnchor="middle"
                  fill="white"
                  fontSize="10"
                  fontWeight="600"
                  style={{ pointerEvents: 'none' }}
                >
                  ROOT
                </text>
              )}

              {/* Address Label */}
              <text
                x={nodePos.x}
                y={nodePos.y + nodeSize / 2 + 20}
                textAnchor="middle"
                fill="#333"
                fontSize="8"
                fontWeight="500"
                style={{ pointerEvents: 'none' }}
              >
                {formatAddress(node.address)}
              </text>

              {/* Amount Label */}
              <text
                x={nodePos.x}
                y={nodePos.y + nodeSize / 2 + 32}
                textAnchor="middle"
                fill="#666"
                fontSize="7"
                fontWeight="400"
                style={{ pointerEvents: 'none' }}
              >
                {formatAmount(node.amount)}
              </text>
            </g>
          );
        })}

        {/* Level Labels */}
        {Array.from(new Set(nodes.map(n => n.level))).map(level => (
          <text
            key={`level-label-${level}`}
            x={80 + (level * 120)}
            y={50}
            textAnchor="middle"
            fill="#666"
            fontSize="12"
            fontWeight="600"
            style={{ pointerEvents: 'none' }}
          >
            Level {level}
          </text>
        ))}
      </svg>

      {/* Node Details Panel */}
      {selectedNode && (
        <div style={{
          position: 'absolute',
          bottom: 10,
          left: 10,
          right: 10,
          backgroundColor: 'white',
          padding: 16,
          borderRadius: 8,
          border: `1px solid ${branding.borderColor}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 10
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ margin: 0, color: branding.primaryColor }}>
              {selectedNode.id === 'root' ? 'Root Address' : 
               selectedNode.id.startsWith('flow-step-') ? `Flow Step ${selectedNode.level}` :
               `Level ${selectedNode.level} Address`}
            </h4>
            <button
              onClick={() => setSelectedNode(null)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 20,
                cursor: 'pointer',
                color: '#666'
              }}
            >
              &times;
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
            <div>
              <strong>Address:</strong>
              <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}>
                {selectedNode.address}
              </div>
            </div>
            <div>
              <strong>Amount:</strong>
              <div style={{ color: selectedNode.direction === 'in' ? '#4CAF50' : '#F44336', marginTop: 4 }}>
                {formatAmount(selectedNode.amount)}
              </div>
            </div>
            <div>
              <strong>Level:</strong>
              <div style={{ marginTop: 4 }}>{selectedNode.level}</div>
            </div>
            <div>
              <strong>Risk Level:</strong>
              <div style={{ 
                color: getRiskColor(selectedNode.riskLevel), 
                fontWeight: 600, 
                marginTop: 4 
              }}>
                {selectedNode.riskLevel}
              </div>
            </div>
            {selectedNode.isExchange && (
              <div>
                <strong>Type:</strong>
                <div style={{ color: '#2196F3', marginTop: 4 }}>🏦 Exchange</div>
              </div>
            )}
            {selectedNode.isSanctioned && (
              <div>
                <strong>Status:</strong>
                <div style={{ color: '#F44336', marginTop: 4 }}>🚨 SANCTIONED</div>
              </div>
            )}
            <div>
              <strong>Timestamp:</strong>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                {new Date(selectedNode.timestamp).toLocaleString()}
              </div>
            </div>
            {selectedNode.transactionHash && (
              <div>
                <strong>Transaction Hash:</strong>
                <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>
                  {selectedNode.transactionHash}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{
        position: 'absolute',
        top: 10,
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'white',
        padding: 8,
        borderRadius: 6,
        border: `1px solid ${branding.borderColor}`,
        fontSize: 12,
        zIndex: 10
      }}>
        <strong>Flow Summary: {nodes.length} Nodes | {connections.length} Connections | {Math.max(...nodes.map(n => n.level))} Levels</strong>
      </div>
    </div>
  );
};

export default CryptoTransactionFlow;
